<template>
  <div class="video-item-wrap">
    <el-popover
      v-if="isPopoverMode"
      :placement="popoverPlacement"
      :trigger="popoverTrigger"
      :width="activeInteractionConfig.popoverWidth || 400"
      :popper-style="popoverStyle"
      :popper-class="popoverClass"
    >
      <template #reference>
        <div
          class="video-container"
          :class="{
            'is-clickable': hasVideoClickAction,
            'is-fullscreen-click': props.config?.enableFullscreen,
            'is-config-click': hasClickConfig,
          }"
          @click="handleContainerClick"
        >
          <video
            v-if="videoSrc"
            ref="videoRef"
            :src="videoSrc"
            :poster="config.poster"
            :controls="config.controls"
            :autoplay="config.autoplay"
            :loop="config.loop"
            :muted="config.muted"
            :style="{ objectFit: config.objectFit || 'contain' }"
            class="video-element"
            :class="{ 'controls-on-top': config.controls }"
            @play="onPlay"
            @pause="onPause"
            @ended="onEnded"
            @error="onError"
            @loadedmetadata="onLoadedMetadata"
          />
          <div v-else class="video-placeholder">
            <span class="video-placeholder-text">视频</span>
          </div>
          <div v-if="hasVideoClickAction" class="video-click-overlay"></div>
        </div>
      </template>
      <PopoverPageRender
        :area-data="activeInteractionConfig.popoverPageData"
        :width="activeInteractionConfig.popoverWidth || 400"
        :background-color="activeInteractionConfig.popoverBackgroundColor || '#ffffff'"
        :border-radius="getPopoverBorderRadius(activeInteractionConfig)"
      />
    </el-popover>

    <div
      v-else
      class="video-container"
      :class="{
        'is-clickable': hasVideoClickAction,
        'is-fullscreen-click': props.config?.enableFullscreen,
        'is-config-click': hasClickConfig,
      }"
      @click="handleContainerClick"
    >
      <video
        v-if="videoSrc"
        ref="videoRef"
        :src="videoSrc"
        :poster="config.poster"
        :controls="config.controls"
        :autoplay="config.autoplay"
        :loop="config.loop"
        :muted="config.muted"
        :style="{ objectFit: config.objectFit || 'contain' }"
        class="video-element"
        :class="{ 'controls-on-top': config.controls }"
        @play="onPlay"
        @pause="onPause"
        @ended="onEnded"
        @error="onError"
        @loadedmetadata="onLoadedMetadata"
      />
      <div v-else class="video-placeholder">
        <span class="video-placeholder-text">视频</span>
      </div>
      <div v-if="hasVideoClickAction" class="video-click-overlay"></div>
    </div>

    <el-dialog
      v-if="activeInteractionConfig && activeInteractionConfig.responseType === 'popup'"
      :class="activeInteractionConfig.popupClass || ''"
      :title="activeInteractionConfig.popupTitle || ''"
      append-to-body
      v-model="dialogTableVisible"
    >
      <template #default>
        <div
          v-if="dialogTableVisible && articleContent && articleContent.content"
          class="new-detail-item-cont"
          v-html="articleContent.content"
        ></div>
      </template>
      <template #footer>
        <el-button type="primary" @click="dialogTableVisible = false">
          {{ getLocaleText('关闭') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from 'vue';
import { resolveBindDataKeyValue } from '@/utils/renderBinding';
import { useRenderConfig } from './uses/useRenderConfig.js';
import PopoverPageRender from '@/components/render/PopoverPageRender.vue';
import { getArticleDetails } from "@/api";
import { useLowCodeActionRuntime } from '@/composables/useLowCodeActionRuntime.js';
import { isLowCodePopupPageAction } from '@/utils/lowCodePopupPage.js';
import useLocate from './uses/useLocate.js';

const { getLocaleText } = useLocate();
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject('getConfigJson', () => ({}));

const props = defineProps({
  currentElementIndex: {
    type: Array,
    default: () => [],
  },
  idx: {
    type: Number,
    default: -1,
  },
  parentKeys: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: true,
  },
  config: {
    type: Object,
  },
});

const videoRef = ref(null);
const dialogTableVisible = ref(false);
const articleContent = ref(null);

const { bindData } = useRenderConfig(() => props.config);

const videoSrc = computed(() => {
  const raw = props.config?.src || props.config?.url || '';
  return resolveBindDataKeyValue(bindData.value, props.config?.bindDataKey, raw);
});

const hasMeaningfulClickConfig = (config) => {
  if (isLowCodePopupPageAction(config)) {
    return true;
  }
  if (!config || !config.responseType) {
    return false;
  }
  if (config.responseType === 'link' || config.responseType === 'jumpLink') {
    return !!config.link;
  }
  if (config.responseType === 'popup') {
    return !!config.article || !!config.popupTitle || !!config.popupClass;
  }
  if (config.responseType === 'popoverPage') {
    return !!config.popoverPageId || !!config.popoverPageTitle || !!config.popoverPageData;
  }
  if (config.responseType === 'openLowCodePopupPage') {
    return !!config.popupPageConfig;
  }
  return true;
};

const hasClickConfig = computed(() => {
  return props.config?.clickConfig && hasMeaningfulClickConfig(props.config.clickConfig);
});

const hasVideoClickAction = computed(() => {
  return hasClickConfig.value || props.config?.enableFullscreen;
});

const activeInteractionConfig = computed(() => {
  return props.config?.clickConfig || null;
});

const isPopoverMode = computed(() => {
  return (
    activeInteractionConfig.value &&
    activeInteractionConfig.value.responseType === 'popoverPage' &&
    !!activeInteractionConfig.value.popoverPageData
  );
});

const popoverPlacement = computed(() => {
  return activeInteractionConfig.value?.popoverPlacement || 'right';
});

const popoverTrigger = computed(() => {
  return activeInteractionConfig.value?.popoverTrigger || 'hover';
});

const popoverClass = computed(() => {
  return ['video-item-popover', activeInteractionConfig.value?.popupClass || ''].filter(Boolean).join(' ');
});

const popoverStyle = computed(() => {
  return {
    padding: '0px',
    border: 'none',
    overflow: 'hidden',
    borderRadius: `${getPopoverBorderRadius(activeInteractionConfig.value)}px`,
    backgroundColor: activeInteractionConfig.value?.popoverBackgroundColor || '#ffffff',
  };
});

function getPopoverBorderRadius(config) {
  return typeof config?.popoverBorderRadius === 'number'
    ? config.popoverBorderRadius
    : 6;
}

const getArticleContent = async () => {
  const interactionConfig = activeInteractionConfig.value;
  if (
    interactionConfig?.responseType !== 'popup' ||
    !interactionConfig?.article?.id
  ) {
    articleContent.value = null;
    return;
  }
  const param = {
    pageNo: 0,
    pageSize: 9999,
    id: interactionConfig.article.id,
    status: "1",
  };
  const res = await getArticleDetails(param);
  if (res.data.dataList) {
    articleContent.value = res.data.dataList[0];
    if (articleContent.value.type === 1 && articleContent.value.content) {
      articleContent.value.content = articleContent.value.content.replace(
        'video',
        'video autoplay '
      );
    }
  } else {
    articleContent.value = null;
  }
};

watch(
  () => activeInteractionConfig.value?.article,
  (value) => {
    if (value || activeInteractionConfig.value?.responseType === 'popup') {
      getArticleContent();
    }
  },
  { deep: true }
);

watch(
  () => activeInteractionConfig.value?.responseType,
  (value) => {
    if (value !== 'popup') {
      dialogTableVisible.value = false;
      articleContent.value = null;
      return;
    }
    getArticleContent();
  },
  { immediate: true }
);

onMounted(() => {
  getArticleContent();
  if (props.config?.enableFullscreen) {
    registerFullscreenListeners();
  }
});

watch(
  () => props.config?.enableFullscreen,
  (value) => {
    if (value) {
      registerFullscreenListeners();
    } else {
      unregisterFullscreenListeners();
    }
  }
);

onBeforeUnmount(() => {
  unregisterFullscreenListeners();
});

const tryPlayVideo = () => {
  const video = videoRef.value;
  if (!video) return;
  video.muted = true;
  const playPromise = video.play();
  if (playPromise && typeof playPromise.then === 'function') {
    playPromise.catch((err) => {
      console.warn('VideoItem: autoplay on fullscreen failed', err);
    });
  }
};

const handleFullscreenChange = () => {
  if (typeof document === 'undefined') return;
  const isFullscreen =
    document.fullscreenElement === videoRef.value ||
    document.webkitFullscreenElement === videoRef.value ||
    document.mozFullScreenElement === videoRef.value ||
    document.msFullscreenElement === videoRef.value;
  if (!isFullscreen) {
    if (videoRef.value && !videoRef.value.paused) {
      videoRef.value.pause();
    }
    return;
  }
  if (videoRef.value && videoRef.value.paused) {
    tryPlayVideo();
  }
};

const fullscreenEventNames = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange',
];

const registerFullscreenListeners = () => {
  if (typeof document === 'undefined') return;
  fullscreenEventNames.forEach((eventName) => {
    document.addEventListener(eventName, handleFullscreenChange);
  });
};

const unregisterFullscreenListeners = () => {
  if (typeof document === 'undefined') return;
  fullscreenEventNames.forEach((eventName) => {
    document.removeEventListener(eventName, handleFullscreenChange);
  });
};

const onPlay = () => {
  console.log('VideoItem: play');
};

const onPause = () => {
  console.log('VideoItem: pause');
};

const onEnded = () => {
  console.log('VideoItem: ended');
};

const onError = (e) => {
  console.error('VideoItem: error', e);
};

const onLoadedMetadata = () => {
  console.log('VideoItem: loadedmetadata');
};

const requestFullscreen = (el) => {
  if (!el) return;
  if (el.requestFullscreen) {
    el.requestFullscreen();
  } else if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
  } else if (el.mozRequestFullScreen) {
    el.mozRequestFullScreen();
  } else if (el.msRequestFullscreen) {
    el.msRequestFullscreen();
  }
};

const handleContainerClick = (event) => {
  if (props.config?.enableFullscreen && videoRef.value) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    requestFullscreen(videoRef.value);
    return;
  }
  if (!hasClickConfig.value) {
    return;
  }
  const interactionConfig = activeInteractionConfig.value;
  if (!interactionConfig) {
    return;
  }
  if (isLowCodePopupPageAction(interactionConfig)) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dispatchLowCodeAction(
      interactionConfig,
      typeof getConfigJson === 'function' ? getConfigJson() : {}
    );
    return;
  }
  if (interactionConfig.responseType === 'popup') {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dialogTableVisible.value = true;
    return;
  }
  if (interactionConfig.responseType === 'jumpLink') {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    window.open(interactionConfig.link, '_blank');
    return;
  }
  if (interactionConfig.responseType === 'link') {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    window.location.href = interactionConfig.link;
    return;
  }
};
</script>

<style scoped>
.video-item-wrap {
  display: block;
  width: 100%;
  height: 100%;
  background: #000;
  min-height: 150px;
  position: relative;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-container.is-clickable {
  cursor: pointer;
}

.video-element {
  display: block;
  width: 100%;
  height: 100%;
  background: #000;
}

.video-click-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
}

.video-click-overlay:hover {
  background: rgba(0, 0, 0, 0.05);
}

.video-element.controls-on-top {
  z-index: 1;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 150px;
  background: #1a1a1a;
  color: #666;
}

.video-placeholder-text {
  font-size: 14px;
}
</style>
